import { useContext, useEffect, HTMLAttributes } from "react";
import { useIntl } from "react-intl";
import SummaryCard from "../summary-card";
import { OrganizationGroup } from "../../../model/OrganizationGroup";
import { Link } from "react-router-dom";
import { ModalKeys } from "../../pages/groups";
import { ButtonGroup } from "react-bootstrap";
import { sortToLatestByCreatedOrUpdated } from "../../../util/objectUtil";
import FieldList from "../../field-list";
import GroupTypeBadge from "../../badges/group-type-badge";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import TooltipTrigger from "../../tooltip-trigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TotalLabel } from "../summary-card/summary-card-view";

interface GroupsCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface GroupsCardProps extends GroupsCardDOMProps {
  isReady: boolean;
  groups?: OrganizationGroup[] | null;
  onLoadGroups?: () => {};

  moreAction: () => void;
}

function GroupsCard(props: GroupsCardProps) {
  const { groups, onLoadGroups, moreAction, isReady } = props;
  const intl = useIntl();
  const { conf } = useContext(UIConfiguration);
  const groupsConf =
    conf.functionality && conf.functionality.groups
      ? conf.functionality.groups
      : ({} as any);
  const headerButton = {
    label: intl.formatMessage({
      defaultMessage: "View",
      description: "View button label",
    }),
    action: moreAction,
  };
  if (!onLoadGroups && groups === undefined) {
    throw new Error(
      "GroupsCard: Required props missing. Either groups or onLoadGroups must be defined"
    );
  }
  const isGroupsUndefined = groups === undefined;
  useEffect(() => {
    if (isGroupsUndefined && onLoadGroups) {
      onLoadGroups();
    }
  }, [isGroupsUndefined, onLoadGroups]);
  return (
    <SummaryCard
      onReloadData={() => {
        if (onLoadGroups) {
          onLoadGroups();
        }
      }}
      isReady={isReady}
      data-test-groups-card
      title={intl.formatMessage({
        defaultMessage: "Groups",
        description: "heading for the card",
      })}
      headerButton={headerButton}
      items={
        groups && groups.length
          ? groups.sort(sortToLatestByCreatedOrUpdated).map((g) => {
              const Wrapper = groupsConf.show ? Link : "span";
              const wrapperProps: any = {
                className:
                  "flex-shrink-1 d-flex justify-content-between align-items-center top-most mr-2",
              };
              if (groupsConf.show) {
                wrapperProps.to =
                  (("/groups/" + g.id) as string) + "/" + ModalKeys.show;
              }
              return (
                <>
                  <Wrapper {...wrapperProps}>
                    <TooltipTrigger
                      tipKey={"group_" + g.id}
                      tip={g.name}
                      placement={"top"}
                    >
                      <span className={"flex-shrink-1 mr-2 truncate"}>
                        {g.name}
                      </span>
                    </TooltipTrigger>
                    <GroupTypeBadge type={g.type} />
                  </Wrapper>
                  {(groupsConf.members || groupsConf.invite) && (
                    <ButtonGroup size={"sm"} className={"ml-auto"}>
                      {groupsConf.members && (
                        <Link
                          className={"btn btn-theme-to-secondary"}
                          to={
                            (("/groups/" + g.id) as string) +
                            "/" +
                            ModalKeys.members
                          }
                        >
                          {intl.formatMessage({
                            defaultMessage: "Members",
                            description: "label for the members button",
                          })}
                        </Link>
                      )}
                      {groupsConf.invite && (
                        <Link
                          className={"btn btn-theme-to-secondary"}
                          to={
                            (("/groups/" + g.id) as string) +
                            "/" +
                            ModalKeys.invite
                          }
                        >
                          {intl.formatMessage({
                            defaultMessage: "Invite",
                            description: "label for the invite button",
                          })}
                        </Link>
                      )}
                    </ButtonGroup>
                  )}
                </>
              );
            })
          : undefined
      }
      footer={
        <FieldList
          inline={true}
          fields={{
            total: {
              label: intl.formatMessage(TotalLabel),
              value: groups ? groups.length : 0,
            },
          }}
        />
      }
    >
      {groups && groups.length === 0 && (
        <div data-test-no-items className={"text-muted text-center py-4"}>
          <div className={"icon pb-1 mb-3"}>
            <span className="fa-layers fa-fw fa-2x">
              <FontAwesomeIcon
                icon="circle"
                mask={"database"}
                className={"opacity-50"}
                transform="shrink-0 right-5 down-5"
              />
              <FontAwesomeIcon icon="ban" transform="shrink-4 right-5 down-5" />
            </span>
          </div>
          <p className={"mb-0"}>
            {intl.formatMessage({
              defaultMessage: "No groups to show.",
              description: "display value for no groups",
            })}
          </p>
        </div>
      )}
    </SummaryCard>
  );
}
export default GroupsCard;
